import { BatchJob, batchJobApi } from '@/services/batchJob.api'
import { formatCronExpression } from '@/utils/cronParser'
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Divider,
  Grid,
  Group,
  Loader,
  Modal,
  Progress,
  Stack,
  Table,
  Text,
  Title
} from '@mantine/core'
import {
  IconAlertTriangle,
  IconCheck,
  IconClock,
  IconDatabaseImport,
  IconEye,
  IconRefresh,
  IconX,
} from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import moment from 'moment-timezone'
import { useEffect, useState } from 'react'

export function BatchJobMonitor() {
  const [batchJobs, setBatchJobs] = useState<BatchJob[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedJob, setSelectedJob] = useState<BatchJob | null>(null)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [startingSync, setStartingSync] = useState(false)

  // Two imports at once would race on the same rows, so the button waits for a running one to finish.
  const inventoryJobRunning = batchJobs.some(
    job => job.status === 'running' && (job.type === 'inventory_sync' || job.type === 'inventory_backfill'),
  )

  const fetchBatchJobs = async () => {
    try {
      setError(null)
      const jobs = await batchJobApi.getBatchJobs({ limit: 100 })
      setBatchJobs(jobs)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch batch jobs')
      console.error('Error fetching batch jobs:', err)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  const handleRefresh = async () => {
    setRefreshing(true)
    await fetchBatchJobs()
  }

  const handleSyncInventory = async () => {
    setStartingSync(true)
    try {
      await batchJobApi.backfillInventory()
      notifications.show({
        title: 'Inventory sync started',
        message: 'It runs in the background; watch this list for progress.',
        color: 'blue',
      })
      await fetchBatchJobs()
    } catch (err) {
      notifications.show({
        title: 'Could not start inventory sync',
        message: err instanceof Error ? err.message : 'Please try again.',
        color: 'red',
      })
    } finally {
      setStartingSync(false)
    }
  }

  const handleViewDetails = async (jobId: string) => {
    try {
      const job = await batchJobApi.getBatchJob(jobId)
      setSelectedJob(job)
      setShowDetailsModal(true)
    } catch (err) {
      console.error('Error fetching job details:', err)
    }
  }

  // Initial load
  useEffect(() => {
    fetchBatchJobs()
  }, [])

  // Auto-refresh every 30 seconds for running jobs
  useEffect(() => {
    const hasRunningJobs = batchJobs.some(job => job.status === 'running')
    if (!hasRunningJobs) return

    const interval = setInterval(() => {
      fetchBatchJobs()
    }, 30000) // 30 seconds

    return () => clearInterval(interval)
  }, [batchJobs])

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running':
        return 'blue'
      case 'completed':
        return 'green'
      case 'failed':
        return 'red'
      case 'scheduled':
        return 'yellow'
      case 'paused':
        return 'orange'
      default:
        return 'gray'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'running':
        return <IconClock size={16} />
      case 'completed':
        return <IconCheck size={16} />
      case 'failed':
        return <IconX size={16} />
      case 'scheduled':
        return <IconClock size={16} />
      case 'paused':
        return <IconClock size={16} />
      default:
        return null
    }
  }

  if (loading) {
    return (
      <Stack gap="md" align="center" py="xl">
        <Loader size="lg" />
        <Text c="dimmed">Loading batch jobs...</Text>
      </Stack>
    )
  }

  if (error) {
    return (
      <Alert icon={<IconAlertTriangle size={16} />} title="Error" color="red">
        <Text>{error}</Text>
        <Button mt="md" leftSection={<IconRefresh size={16} />} onClick={handleRefresh}>
          Retry
        </Button>
      </Alert>
    )
  }

  return (
    <Stack gap="md">
      <Card withBorder>
        <Group justify="space-between" mb="md">
          <Title order={4}>Job Status</Title>
          <Group>
            <Button
              leftSection={startingSync ? <Loader size={14} /> : <IconDatabaseImport size={16} />}
              variant="outline"
              size="sm"
              onClick={handleSyncInventory}
              disabled={startingSync || inventoryJobRunning}
              title={inventoryJobRunning ? 'An inventory import is already running' : 'Import every inventory file from SFTP now'}
            >
              Sync Inventory
            </Button>
            <Button 
              leftSection={refreshing ? <Loader size={14} /> : <IconRefresh size={16} />} 
              variant="outline" 
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              Refresh
            </Button>
          </Group>
        </Group>

        {batchJobs.length === 0 ? (
          <Alert title="No Jobs Found" color="gray">
            <Text size="sm">No batch jobs have been executed yet.</Text>
          </Alert>
        ) : (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Job Name</Table.Th>
                <Table.Th>Type</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Progress</Table.Th>
                <Table.Th>Last Run</Table.Th>
                <Table.Th>Records</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {batchJobs.map((job) => (
                <Table.Tr key={job.id}>
                  <Table.Td>
                    <Text fw={500}>{job.name}</Text>
                    {job.schedule && (
                      <Text size="xs" c="dimmed">
                        {formatCronExpression(job.schedule)}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="outline" size="sm">
                      {job.type.replace('_', ' ').toUpperCase()}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      color={getStatusColor(job.status)}
                      leftSection={getStatusIcon(job.status)}
                    >
                      {job.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <Progress
                        value={job.progress}
                        size="sm"
                        style={{ width: 100 }}
                        color={getStatusColor(job.status)}
                      />
                      <Text size="sm">{job.progress}%</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {moment(job.startedAt).format('MMM D, YYYY h:mm A')}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {moment(job.startedAt).fromNow()}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {job.recordsProcessed !== undefined && job.totalRecords !== undefined
                        ? `${job.recordsProcessed}/${job.totalRecords}`
                        : job.recordsProcessed !== undefined
                        ? String(job.recordsProcessed)
                        : '-'
                      }
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        onClick={() => handleViewDetails(job.id)}
                      >
                        <IconEye size={16} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Card>

      <Modal
        opened={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title={`${selectedJob?.name} Details`}
        size="lg"
      >
        {selectedJob && (
          <Stack gap="md">
            <Grid>
              <Grid.Col span={6}>
                <Text fw={500}>Job Name</Text>
                <Text size="sm" c="dimmed">{selectedJob.name}</Text>
              </Grid.Col>
              <Grid.Col span={6}>
                <Text fw={500}>Type</Text>
                <Text size="sm" c="dimmed">{selectedJob.type}</Text>
              </Grid.Col>
              <Grid.Col span={6}>
                <Text fw={500}>Status</Text>
                <Badge
                  color={getStatusColor(selectedJob.status)}
                  leftSection={getStatusIcon(selectedJob.status)}
                >
                  {selectedJob.status}
                </Badge>
              </Grid.Col>
              <Grid.Col span={6}>
                <Text fw={500}>Progress</Text>
                <Text size="sm" c="dimmed">{selectedJob.progress}%</Text>
              </Grid.Col>
              {selectedJob.schedule && (
                <Grid.Col span={6}>
                  <Text fw={500}>Schedule</Text>
                  <Text size="sm" c="dimmed">{formatCronExpression(selectedJob.schedule)}</Text>
                </Grid.Col>
              )}
            </Grid>

            <Divider />

            <Text fw={500}>Timing</Text>
            <Grid>
              <Grid.Col span={6}>
                <Text size="sm" c="dimmed">Started</Text>
                <Text size="sm">
                  {moment(selectedJob.startedAt).format('MMM D, YYYY h:mm:ss A')}
                </Text>
                <Text size="xs" c="dimmed">
                  {moment(selectedJob.startedAt).fromNow()}
                </Text>
              </Grid.Col>
              {selectedJob.completedAt && (
                <Grid.Col span={6}>
                  <Text size="sm" c="dimmed">Completed</Text>
                  <Text size="sm">
                    {moment(selectedJob.completedAt).format('MMM D, YYYY h:mm:ss A')}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {moment(selectedJob.completedAt).fromNow()}
                  </Text>
                </Grid.Col>
              )}
            </Grid>

            {selectedJob.error && (
              <>
                <Divider />
                <Text fw={500}>Error Details</Text>
                <Alert icon={<IconAlertTriangle size={16} />} title="Error" color="red">
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{selectedJob.error}</Text>
                  {selectedJob.errorStack && (
                    <Text size="xs" c="dimmed" mt="xs" style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                      {selectedJob.errorStack}
                    </Text>
                  )}
                </Alert>
              </>
            )}

            {(selectedJob.recordsProcessed !== undefined || selectedJob.totalRecords !== undefined) && (
              <>
                <Divider />
                <Text fw={500}>Processing Statistics</Text>
                <Grid>
                  {selectedJob.recordsProcessed !== undefined && (
                    <Grid.Col span={6}>
                      <Text size="sm" c="dimmed">Records Processed</Text>
                      <Text size="sm">{selectedJob.recordsProcessed}</Text>
                    </Grid.Col>
                  )}
                  {selectedJob.totalRecords !== undefined && (
                    <Grid.Col span={6}>
                      <Text size="sm" c="dimmed">Total Records</Text>
                      <Text size="sm">{selectedJob.totalRecords}</Text>
                    </Grid.Col>
                  )}
                  {selectedJob.successCount !== undefined && (
                    <Grid.Col span={6}>
                      <Text size="sm" c="dimmed">Success Count</Text>
                      <Text size="sm" c="green">{selectedJob.successCount}</Text>
                    </Grid.Col>
                  )}
                  {selectedJob.failureCount !== undefined && (
                    <Grid.Col span={6}>
                      <Text size="sm" c="dimmed">Failure Count</Text>
                      <Text size="sm" c="red">{selectedJob.failureCount}</Text>
                    </Grid.Col>
                  )}
                </Grid>
              </>
            )}

            {selectedJob.metadata && (
              <>
                <Divider />
                <Text fw={500}>Metadata</Text>
                <Text size="sm" c="dimmed" style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                  {JSON.stringify(selectedJob.metadata, null, 2)}
                </Text>
              </>
            )}
          </Stack>
        )}
      </Modal>
    </Stack>
  )
}
